import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import './App.css';
import './ExperimentsView.css';
import './TaskDetailView.css';
import {
    IExperiment,
    IReportExperiment,
    getStatusColor,
    getExperimentTypeIcon,
    formatDateTime
} from './types/experiment';
import {
    ExperimentProgress,
    ExperimentValidationResults,
    ExperimentExecutionControls
} from './components/experiments';

interface ReportExperimentsResponse {
    experiments: IReportExperiment[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

const ExperimentDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { token, authLoaded, user } = useAuth();
    const { t } = useTranslation();

    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;

    const [experiment, setExperiment] = useState<IExperiment | null>(null);
    const [reportExperiments, setReportExperiments] = useState<IReportExperiment[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [reportPage, setReportPage] = useState<number>(1);
    const [reportPagination, setReportPagination] = useState<{ total: number; totalPages: number }>({
        total: 0,
        totalPages: 0
    });
    const [activeTab, setActiveTab] = useState<'overview' | 'results' | 'validation'>('overview');

    const fetchExperiment = useCallback(async () => {
        if (!id) {
            setError('No experiment ID provided');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const headers: Record<string, string> = {
                'x-api-key': apiKey
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await axios.get<{ experiment: IExperiment }>(`${apiUrl}/admin/experiments/${id}`, { headers });
            setExperiment(response.data.experiment);
            setError(null);
        } catch (err: any) {
            console.error('Error fetching experiment:', err);
            if (err.response?.status === 404) {
                setError(t('experiments.notFound'));
            } else if (err.response?.status === 403) {
                setError(t('experiments.accessDenied'));
            } else {
                setError(t('experiments.loadError'));
            }
        } finally {
            setLoading(false);
        }
    }, [id, token, apiKey, apiUrl, t]);

    const fetchReportExperiments = useCallback(async (page: number = 1) => {
        if (!experiment?.experiment_id) return;

        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await axios.get<ReportExperimentsResponse>(
                `${apiUrl}/report-experiments?experiment_id=${encodeURIComponent(experiment.experiment_id)}&page=${page}&limit=20`,
                { headers }
            );

            setReportExperiments(response.data.experiments);
            setReportPagination({
                total: response.data.pagination.total,
                totalPages: response.data.pagination.totalPages
            });
        } catch (err: any) {
            console.error('Error fetching report experiments:', err);
        }
    }, [experiment?.experiment_id, token, apiKey, apiUrl]);

    useEffect(() => {
        if (!authLoaded) return;
        fetchExperiment();
    }, [authLoaded, fetchExperiment]);

    useEffect(() => {
        if (experiment && activeTab === 'results') {
            fetchReportExperiments(reportPage);
        }
    }, [experiment, activeTab, reportPage, fetchReportExperiments]);

    const handleBack = () => {
        const params = searchParams.toString();
        navigate(`/experiments${params ? `?${params}` : ''}`);
    };

    const handleEdit = () => {
        navigate(`/experiments/${id}/edit`);
    };

    const handleStartExperiment = async () => {
        if (!id) return;
        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            await axios.post(`${apiUrl}/admin/experiments/${id}/start`, {}, { headers });
            await fetchExperiment();
        } catch (err: any) {
            console.error('Error starting experiment:', err);
            setError(t('experiments.execution.startError'));
        }
    };

    const handlePauseExperiment = async () => {
        if (!id) return;
        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            await axios.post(`${apiUrl}/admin/experiments/${id}/pause`, {}, { headers });
            await fetchExperiment();
        } catch (err: any) {
            console.error('Error pausing experiment:', err);
            setError(t('experiments.execution.pauseError'));
        }
    };

    const handleResumeExperiment = async () => {
        if (!id) return;
        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            await axios.post(`${apiUrl}/admin/experiments/${id}/resume`, {}, { headers });
            await fetchExperiment();
        } catch (err: any) {
            console.error('Error resuming experiment:', err);
            setError(t('experiments.execution.resumeError'));
        }
    };

    const handleStopExperiment = async () => {
        if (!id) return;
        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            await axios.post(`${apiUrl}/admin/experiments/${id}/stop`, {}, { headers });
            await fetchExperiment();
        } catch (err: any) {
            console.error('Error stopping experiment:', err);
            setError(t('experiments.execution.stopError'));
        }
    };

    const getConfidenceColor = (confidence: number): string => {
        if (confidence >= 0.8) return '#22c55e';
        if (confidence >= 0.6) return '#eab308';
        if (confidence >= 0.4) return '#f97316';
        return '#ef4444';
    };

    if (!authLoaded || loading) {
        return (
            <div className="loading-container">
                <div className="bonds-loader-flex">
                    <span className="material-icons loader-icon rotating">sync</span>
                    <span className="loader-text">{t('loading.experimentDetails')}</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-message">
                    <span className="material-icons" style={{ marginRight: '10px', color: '#e74c3c' }}>error</span>
                    {error}
                </div>
                <button onClick={handleBack} className="btn-primary" style={{ marginTop: '20px' }}>
                    <span className="material-icons" style={{ marginRight: '8px' }}>arrow_back</span>
                    {t('buttons.backToExperiments')}
                </button>
            </div>
        );
    }

    if (!experiment) {
        return (
            <div className="error-container">
                <div className="error-message">{t('experiments.notFound')}</div>
                <button onClick={handleBack} className="btn-primary" style={{ marginTop: '20px' }}>
                    <span className="material-icons" style={{ marginRight: '8px' }}>arrow_back</span>
                    {t('buttons.backToExperiments')}
                </button>
            </div>
        );
    }

    const expData = experiment.experiment_data;
    const status = experiment.progress?.run_status || expData?.status || 'draft';
    const type = expData?.type || 'unknown';

    return (
        <div className="task-detail-view">
            {/* Header */}
            <div className="task-detail-header">
                <button className="back-btn" onClick={handleBack}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <div className="header-info">
                    <h1>
                        <span className="material-icons" style={{ marginRight: '8px', verticalAlign: 'middle' }}>{getExperimentTypeIcon(type)}</span>
                        {experiment.experiment_id || t('experiments.unnamed')}
                    </h1>
                    <div className="header-meta">
                        <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(status) }}
                        >
                            {status === 'running' && <span className="status-spinner" />}
                            {t(`experiments.status.${status}`) || status}
                        </span>
                        <span className="group-badge">
                            {type}
                        </span>
                        {expData?.provider && (
                            <span className="group-badge">
                                {expData.provider}
                            </span>
                        )}
                    </div>
                </div>
                <div className="header-actions">
                    {user?.role === 'ADMIN' && (
                        <button onClick={handleEdit} className="btn-secondary">
                            <span className="material-icons">edit</span>
                            {t('buttons.edit')}
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="detail-tabs">
                <button
                    className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    <span className="material-icons">info</span>
                    {t('experiments.tabs.overview')}
                </button>
                <button
                    className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
                    onClick={() => setActiveTab('results')}
                >
                    <span className="material-icons">description</span>
                    {t('experiments.tabs.results')}
                    {reportPagination.total > 0 && (
                        <span className="tab-count">{reportPagination.total}</span>
                    )}
                </button>
                <button
                    className={`tab-btn ${activeTab === 'validation' ? 'active' : ''}`}
                    onClick={() => setActiveTab('validation')}
                >
                    <span className="material-icons">fact_check</span>
                    {t('experiments.tabs.validation')}
                </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="overview-tab">
                        <div className="detail-grid">
                            {/* Configuration Section */}
                            <div className="detail-section">
                                <h3>{t('experiments.sections.configuration')}</h3>
                                <div className="detail-items">
                                    <div className="detail-item">
                                        <span className="detail-label">{t('experiments.fields.experimentId')}</span>
                                        <span className="detail-value">{experiment.experiment_id}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('experiments.fields.type')}</span>
                                        <span className="detail-value">{type}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('experiments.fields.provider')}</span>
                                        <span className="detail-value">{expData?.provider || '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('experiments.fields.model')}</span>
                                        <span className="detail-value">{expData?.model || '-'}</span>
                                    </div>
                                    {expData?.endpoint && (
                                        <div className="detail-item">
                                            <span className="detail-label">{t('experiments.fields.endpoint')}</span>
                                            <span className="detail-value truncate">{expData.endpoint}</span>
                                        </div>
                                    )}
                                    <div className="detail-item">
                                        <span className="detail-label">{t('experiments.fields.contextSize')}</span>
                                        <span className="detail-value">{expData?.context_size || '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('experiments.fields.reasoning')}</span>
                                        <span className="detail-value">
                                            {expData?.reasoning ? t('common.yes') : t('common.no')}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('experiments.fields.strict')}</span>
                                        <span className="detail-value">
                                            {expData?.strict ? t('common.yes') : t('common.no')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Execution Controls Section */}
                            <div className="detail-section">
                                <h3>{t('experiments.sections.execution')}</h3>
                                <ExperimentExecutionControls
                                    experiment={experiment}
                                    onStart={handleStartExperiment}
                                    onPause={handlePauseExperiment}
                                    onResume={handleResumeExperiment}
                                    onStop={handleStopExperiment}
                                    disabled={user?.role !== 'ADMIN'}
                                />
                            </div>

                            {/* Progress Section */}
                            {experiment.progress && (
                                <div className="detail-section full-width">
                                    <h3>{t('experiments.sections.progress')}</h3>
                                    <ExperimentProgress progress={experiment.progress} showDetails />
                                </div>
                            )}

                            {/* Statistics Section */}
                            <div className="detail-section">
                                <h3>{t('experiments.sections.statistics')}</h3>
                                <div className="detail-items">
                                    <div className="detail-item">
                                        <span className="detail-label">{t('experiments.fields.processedDocuments')}</span>
                                        <span className="detail-value large">
                                            {expData?.number_of_processed_documents || 0}
                                        </span>
                                    </div>
                                    {experiment.overall_average_confidence !== undefined && (
                                        <div className="detail-item">
                                            <span className="detail-label">{t('experiments.fields.avgConfidence')}</span>
                                            <span
                                                className="detail-value large"
                                                style={{ color: getConfidenceColor(experiment.overall_average_confidence) }}
                                            >
                                                {(experiment.overall_average_confidence * 100).toFixed(1)}%
                                            </span>
                                        </div>
                                    )}
                                    {experiment.overall_validation_rate !== undefined && (
                                        <div className="detail-item">
                                            <span className="detail-label">{t('experiments.fields.validationRate')}</span>
                                            <span className="detail-value large">
                                                {(experiment.overall_validation_rate * 100).toFixed(1)}%
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Tags Section */}
                            {expData?.tags && expData.tags.length > 0 && (
                                <div className="detail-section">
                                    <h3>{t('experiments.sections.tags')}</h3>
                                    <div className="tags-container">
                                        {expData.tags.map((tag, index) => (
                                            <span key={index} className="tag-badge">{tag}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Timestamps Section */}
                            <div className="detail-section">
                                <h3>{t('experiments.sections.timestamps')}</h3>
                                <div className="detail-items">
                                    <div className="detail-item">
                                        <span className="detail-label">{t('experiments.fields.createdAt')}</span>
                                        <span className="detail-value">
                                            {formatDateTime(experiment.experiment_created_time)}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('experiments.fields.lastUsed')}</span>
                                        <span className="detail-value">
                                            {formatDateTime(experiment.last_used_timestamp)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Machine Info Section - Admin Only */}
                            {user?.role === 'ADMIN' && experiment.machine_info && (
                                <div className="detail-section">
                                    <h3>{t('experiments.sections.machineInfo')}</h3>
                                    <div className="detail-items">
                                        <div className="detail-item">
                                            <span className="detail-label">{t('experiments.fields.hostname')}</span>
                                            <span className="detail-value">{experiment.machine_info.hostname}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">{t('experiments.fields.platform')}</span>
                                            <span className="detail-value">{experiment.machine_info.platform}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">{t('experiments.fields.system')}</span>
                                            <span className="detail-value">{experiment.machine_info.system}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Prompt Section */}
                            {expData?.prompt && (
                                <div className="detail-section full-width">
                                    <h3>{t('experiments.sections.prompt')}</h3>
                                    <pre className="prompt-display">{expData.prompt}</pre>
                                </div>
                            )}

                            {/* JSON Schema Section */}
                            {expData?.json_schema && (
                                <div className="detail-section full-width">
                                    <h3>{t('experiments.sections.jsonSchema')}</h3>
                                    <pre className="json-schema-display">{expData.json_schema}</pre>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Results Tab */}
                {activeTab === 'results' && (
                    <div className="results-tab">
                        {reportExperiments.length === 0 ? (
                            <div className="empty-state">
                                <span className="material-icons">description</span>
                                <p>{t('experiments.results.noResults')}</p>
                            </div>
                        ) : (
                            <>
                                <table className="results-table">
                                    <thead>
                                        <tr>
                                            <th>{t('experiments.results.recordId')}</th>
                                            <th>{t('experiments.results.status')}</th>
                                            <th>{t('experiments.results.processingTime')}</th>
                                            <th>{t('experiments.results.createdAt')}</th>
                                            <th>{t('experiments.results.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportExperiments.map((reportExp) => (
                                            <tr key={reportExp._id}>
                                                <td className="record-id">
                                                    <Link to={`/report-sources?doc=${reportExp.record_id}`}>
                                                        {reportExp.record_id}
                                                    </Link>
                                                </td>
                                                <td>
                                                    <span className={`status-badge small ${reportExp.status || 'unknown'}`}>
                                                        {reportExp.status || 'unknown'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {reportExp.processing_time
                                                        ? `${(reportExp.processing_time / 1000).toFixed(2)}s`
                                                        : '-'}
                                                </td>
                                                <td>{formatDateTime(reportExp.created_at)}</td>
                                                <td>
                                                    <Link
                                                        to={`/report-sources?doc=${reportExp.record_id}`}
                                                        className="action-link"
                                                    >
                                                        <span className="material-icons">visibility</span>
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Pagination */}
                                {reportPagination.totalPages > 1 && (
                                    <div className="pagination">
                                        <button
                                            className="pagination-btn"
                                            disabled={reportPage === 1}
                                            onClick={() => setReportPage(reportPage - 1)}
                                        >
                                            <span className="material-icons">chevron_left</span>
                                        </button>
                                        <span className="pagination-info">
                                            {t('pagination.page', { page: reportPage, total: reportPagination.totalPages })}
                                        </span>
                                        <button
                                            className="pagination-btn"
                                            disabled={reportPage === reportPagination.totalPages}
                                            onClick={() => setReportPage(reportPage + 1)}
                                        >
                                            <span className="material-icons">chevron_right</span>
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Validation Tab */}
                {activeTab === 'validation' && (
                    <div className="validation-tab">
                        <ExperimentValidationResults experiment={experiment} showFullDetails />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExperimentDetailView;
